import { Drawer } from "../../components/ui/drawer";
import { Popover } from "../../components/ui/popover";
import { useControllableFlag } from "../../hooks/use-controllable-flag";
import { useControllableState } from "../../hooks/use-controllable-state";
import * as React from "react";
import {
  EntitySelectorProvider,
  filterEntityItems,
  isItemCollapsed,
  type EntitySelectorConfig,
  type EntitySelectorContextValue,
  type EntitySelectorVariant,
  type EntityItemConfig,
  type EntityPoolConfig,
  type EntitySelection,
  type EntityBadgeConfig,
} from "./entity-selector-context";
import type { Omittable } from "../../lib/props";

// One shared empty list, so "this pool has no items" is a stable reference and
// the effects keyed on it do not re-run every render.
const EMPTY_ITEMS: EntityItemConfig[] = [];

// Imperative handle for driving keyboard nav from outside the selector subtree
// (e.g. a TipTap mention menu forwarding arrows/Enter while focus stays in the editor).
export type EntitySelectorHandle = {
  move: (delta: number) => void;
  selectActive: () => void;
  getActiveId: () => string | null;
  /** Switch the active pool (tab) by offset, wrapping. No-op with <2 pools. */
  cycleActivePool: (delta: number) => void;
};

// Drawer presentation: half and near-full snaps, opening at the tall one so
// the search input + list are immediately usable.
const DRAWER_SNAP_POINTS = [0.5, 0.92];

export type EntitySelectorRootProps = Omittable<{
  config: EntitySelectorConfig;
  value?: EntitySelection[];
  onValueChange?: (selections: EntitySelection[]) => void;
  defaultValue?: EntitySelection[];
  search?: string;
  onSearchChange?: (search: string) => void;
  /** Controlled open state. Pair with `onOpenChange` to fully drive the popover/drawer from outside. */
  open?: boolean;
  defaultOpen?: boolean;
  /** Notified whenever the popover/drawer opens or closes. */
  onOpenChange?: (open: boolean) => void;
  variant?: EntitySelectorVariant;
  /** Called before a selection is applied. Return `false` to veto — the popover closes but the displayed value stays unchanged. */
  onBeforeSelect?: (entity: EntityItemConfig, poolId: string) => boolean;
  ref?: React.Ref<EntitySelectorHandle>;
  children: React.ReactNode;
}>;

function EntitySelectorRoot({
  config,
  value: controlledValue,
  onValueChange,
  defaultValue,
  search: controlledSearch,
  onSearchChange,
  open: controlledOpen,
  defaultOpen,
  onOpenChange,
  variant = "popover",
  onBeforeSelect,
  ref,
  children,
}: EntitySelectorRootProps) {
  const [selections, setSelections] = useControllableState<EntitySelection[]>({
    prop: controlledValue,
    defaultProp: defaultValue ?? [],
    onChange: onValueChange,
  });

  const [open, setOpenFlag] = useControllableFlag({
    prop: controlledOpen,
    defaultProp: defaultOpen,
    onChange: onOpenChange,
  });

  const defaultPoolId = config.defaultPool ?? config.pools[0]?.id ?? "";
  const [activePool, setActivePool] = React.useState(defaultPoolId);
  const [search, setSearch] = useControllableState<string>({
    prop: controlledSearch,
    defaultProp: "",
    onChange: onSearchChange,
  });

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!next) setSearch("");
      setOpenFlag(next);
    },
    [setOpenFlag, setSearch],
  );

  const isInline = variant === "inline";
  const effectiveOpen = isInline ? true : open;
  const inputRef = React.useRef<HTMLInputElement>(null);

  const focusInput = React.useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const toggleSelection = React.useCallback(
    (entity: EntityItemConfig, poolId: string) => {
      const exists = selections.find((s) => s.id === entity.id);
      // A disabled entity can be SHOWN as the current value but never newly
      // chosen, so an already-selected one still toggles off and the rest are
      // inert. Guarded here rather than on the row, because the keyboard path
      // (`selectActive`) never passes through a row's pointer handlers — leaving
      // Enter able to select what a click cannot.
      if (entity.disabled && !exists) return;

      // Allow callers to veto the selection (e.g. to open a confirmation dialog instead)
      if (onBeforeSelect && !onBeforeSelect(entity, poolId)) {
        setOpen(false);
        return;
      }

      if (config.maxSelections === 1) {
        // Single-select: replace (or keep) current selection and auto-close
        if (!exists) {
          setSelections([{ id: entity.id, poolId, entity }]);
        }
        setOpen(false);
        return;
      }
      if (exists) {
        setSelections(selections.filter((s) => s.id !== entity.id));
      } else if (
        config.maxSelections &&
        selections.length >= config.maxSelections
      ) {
        return;
      } else {
        setSelections([...selections, { id: entity.id, poolId, entity }]);
      }
    },
    [config.maxSelections, selections, setSelections, setOpen, onBeforeSelect],
  );

  const isSelected = React.useCallback(
    (entityId: string) => {
      return selections.some((s) => s.id === entityId);
    },
    [selections],
  );

  const clearSelections = React.useCallback(() => {
    setSelections([]);
  }, [setSelections]);

  const getPool = React.useCallback(
    (poolId: string): EntityPoolConfig | undefined => {
      return config.pools.find((p) => p.id === poolId);
    },
    [config.pools],
  );

  const getPoolBadge = React.useCallback(
    (poolId: string): EntityBadgeConfig | undefined => {
      return getPool(poolId)?.itemBadge;
    },
    [getPool],
  );

  // Notify pools when the search query changes so they can fetch matching items from the server.
  // Use a ref for pools to avoid re-triggering when pool objects update after search results
  // arrive (which would cause an infinite fetch loop: search → new items → new pool ref → re-fire effect).
  const poolsRef = React.useRef(config.pools);
  poolsRef.current = config.pools;

  React.useEffect(() => {
    for (const pool of poolsRef.current) {
      pool.onSearchChange?.(search);
    }
  }, [search]);

  // Per-pool filter cache keyed on (pool.items ref, search) so a pool
  // whose items are unchanged isn't re-scanned when another pool's hook emits a
  // new items ref. serverFiltered pools skip client filtering entirely.
  const poolFilterCacheRef = React.useRef(
    new Map<
      string,
      { items: EntityItemConfig[]; search: string; filtered: EntityItemConfig[] }
    >(),
  );

  const filteredItemsCache = React.useMemo(() => {
    const cache = new Map<string, EntityItemConfig[]>();
    const prev = poolFilterCacheRef.current;
    const next = new Map<
      string,
      { items: EntityItemConfig[]; search: string; filtered: EntityItemConfig[] }
    >();
    for (const pool of config.pools) {
      const entry = prev.get(pool.id);
      let filtered: EntityItemConfig[];
      if (pool.serverFiltered) {
        filtered = pool.items;
      } else if (
        entry &&
        entry.items === pool.items &&
        entry.search === search
      ) {
        filtered = entry.filtered;
      } else {
        filtered = filterEntityItems(pool.items, search);
      }
      next.set(pool.id, {
        items: pool.items,
        search,
        filtered,
      });
      cache.set(pool.id, filtered);
    }
    poolFilterCacheRef.current = next;
    return cache;
  }, [config.pools, search]);

  const getFilteredItems = React.useCallback(
    (poolId: string): EntityItemConfig[] =>
      filteredItemsCache.get(poolId) ?? [],
    [filteredItemsCache],
  );

  // Pinned rows, search-filtered per pool. Computed HERE (not in the pool
  // renderer) because they participate in keyboard navigation: they render
  // above the list, so the nav list is pinned + filtered and pinned rows own
  // indices 0..n-1. Keeping the pool's render source and the nav list on the
  // same memo means the highlight can never point at a row the pool isn't
  // showing.
  const pinnedItemsCache = React.useMemo(() => {
    const cache = new Map<string, EntityItemConfig[]>();
    for (const pool of config.pools) {
      cache.set(
        pool.id,
        pool.pinnedItems?.length
          ? filterEntityItems(pool.pinnedItems, search)
          : [],
      );
    }
    return cache;
  }, [config.pools, search]);

  const getPinnedItems = React.useCallback(
    (poolId: string): EntityItemConfig[] => pinnedItemsCache.get(poolId) ?? [],
    [pinnedItemsCache],
  );

  // ── Collapsed sections ──────────────────────────────────────────────────
  // Held HERE rather than in the pool renderer for the same reason pinned rows
  // are: a folded section's rows are dropped from the nav list below, and only
  // one owner can keep "what is rendered" and "what the cursor can reach" in
  // agreement. Keyed per pool, so two pools sharing a section label fold
  // independently. Browse and search keep separate sets (see the context docs).
  const defaultCollapsed = React.useMemo(() => {
    const byPool = new Map<string, string[]>();
    for (const pool of config.pools) {
      if (pool.defaultCollapsedSections?.length)
        byPool.set(pool.id, pool.defaultCollapsedSections);
    }
    return byPool;
  }, [config.pools]);

  const [collapsedSections, setCollapsedSections] = React.useState<
    Map<string, Set<string>>
  >(() => new Map([...defaultCollapsed].map(([id, keys]) => [id, new Set(keys)])));
  const [searchCollapsed, setSearchCollapsed] = React.useState<
    Map<string, Set<string>>
  >(() => new Map());

  // Each default label is applied exactly once. Seeding the state above and
  // stopping there ignored a collapse set that only exists after an async query
  // resolves — the automation pickers derive their provider sections from one, so
  // a dropdown opened on a cold cache came up fully expanded. Re-applying the
  // whole set whenever it changes would instead re-fold a section the user just
  // opened, so the applied labels are remembered and only new ones are folded.
  const appliedDefaultsRef = React.useRef<Map<string, Set<string>> | null>(null);
  React.useEffect(() => {
    const applied = (appliedDefaultsRef.current ??= new Map(
      [...defaultCollapsed].map(([id, keys]) => [id, new Set(keys)]),
    ));
    const fresh = new Map<string, string[]>();
    for (const [poolId, keys] of defaultCollapsed) {
      const seen = applied.get(poolId);
      const added = seen ? keys.filter((key) => !seen.has(key)) : keys;
      if (added.length) fresh.set(poolId, added);
    }
    if (fresh.size === 0) return;
    for (const [poolId, keys] of fresh) {
      const seen = applied.get(poolId) ?? new Set<string>();
      for (const key of keys) seen.add(key);
      applied.set(poolId, seen);
    }
    setCollapsedSections((prev) => {
      const next = new Map(prev);
      for (const [poolId, keys] of fresh) {
        const set = new Set(next.get(poolId) ?? []);
        for (const key of keys) set.add(key);
        next.set(poolId, set);
      }
      return next;
    });
  }, [defaultCollapsed]);

  const isSearching = search.trim().length > 0;
  React.useEffect(() => {
    if (!isSearching) {
      setSearchCollapsed((prev) => (prev.size ? new Map() : prev));
    }
  }, [isSearching]);

  const activeCollapsed = isSearching ? searchCollapsed : collapsedSections;
  const isSectionCollapsed = React.useCallback(
    (poolId: string, key: string) => !!activeCollapsed.get(poolId)?.has(key),
    [activeCollapsed],
  );
  const toggleSection = React.useCallback(
    (poolId: string, key: string) => {
      const setState = isSearching ? setSearchCollapsed : setCollapsedSections;
      setState((prev) => {
        const next = new Map(prev);
        const set = new Set(next.get(poolId) ?? []);
        if (set.has(key)) set.delete(key);
        else set.add(key);
        next.set(poolId, set);
        return next;
      });
    },
    [isSearching],
  );

  // The keyboard-nav list: pinned rows first, then the regular filtered list —
  // matching visual order in the pool.
  const navItemsCache = React.useMemo(() => {
    const cache = new Map<string, EntityItemConfig[]>();
    for (const pool of config.pools) {
      // Both caches are keyed by this same pool list, so every id is present.
      const pinned = pinnedItemsCache.get(pool.id)!;
      const filtered = filteredItemsCache.get(pool.id)!;
      // Rows under a folded heading are not rendered, so they are not navigable
      // either.
      const closed = pool.collapsibleSections
        ? activeCollapsed.get(pool.id)
        : undefined;
      const visible = closed?.size
        ? filtered.filter((item) => !isItemCollapsed(item, (key) => closed.has(key)))
        : filtered;
      cache.set(pool.id, pinned.length ? [...pinned, ...visible] : visible);
    }
    return cache;
  }, [config.pools, pinnedItemsCache, filteredItemsCache, activeCollapsed]);

  // Memoized because effects below depend on it: the `?? []` fallback mints a
  // fresh array every render, which would re-run them on every render.
  const activeItems = React.useMemo(
    () => navItemsCache.get(activePool) ?? EMPTY_ITEMS,
    [navItemsCache, activePool],
  );
  const [activeIndex, setActiveIndex] = React.useState(0);

  // Which input last moved the cursor. Only the pointer path sets this true, and
  // every other path clears it, so the pool can scroll the active row into view
  // for the keyboard and leave the list alone for hover. See
  // `isActiveFromPointer` on the context for why that distinction matters.
  const activeFromPointerRef = React.useRef(false);
  const activateFromPointer = React.useCallback((index: number) => {
    activeFromPointerRef.current = true;
    setActiveIndex(index);
  }, []);
  const isActiveFromPointer = React.useCallback(
    () => activeFromPointerRef.current,
    [],
  );

  // Reset highlight on query/pool change. Items arriving async (same query) keep
  // index 0 and are clamped at use.
  React.useEffect(() => {
    activeFromPointerRef.current = false;
    setActiveIndex(0);
  }, [search, activePool]);

  // Open with the cursor on the current selection (combobox convention) —
  // otherwise row 0 carries the keyboard highlight while the checked row
  // carries the selection highlight, reading as two highlighted items. The ref
  // gates this to once per open so it still fires when pool items arrive async
  // after the popover mounts, but never yanks the cursor afterwards. A search
  // typed before items arrive consumes the gate: the first-match reset wins.
  const cursorInitRef = React.useRef(false);
  React.useEffect(() => {
    if (!effectiveOpen) {
      cursorInitRef.current = false;
      return;
    }
    if (cursorInitRef.current) return;
    if (search) {
      cursorInitRef.current = true;
      return;
    }
    if (activeItems.length === 0) return;
    cursorInitRef.current = true;
    const idx = activeItems.findIndex((item) =>
      selections.some((s) => s.id === item.id),
    );
    // Opening ON the selection is meant to scroll it into view, so this is not
    // a pointer activation.
    activeFromPointerRef.current = false;
    setActiveIndex(idx >= 0 ? idx : 0);
  }, [effectiveOpen, search, activeItems, selections]);

  const clampedActiveIndex =
    activeItems.length === 0
      ? 0
      : Math.min(activeIndex, activeItems.length - 1);
  const activeId = activeItems[clampedActiveIndex]?.id ?? null;

  const moveActive = React.useCallback(
    (delta: number) => {
      const n = activeItems.length;
      if (n === 0) return;
      activeFromPointerRef.current = false;
      setActiveIndex((i) => (((i + delta) % n) + n) % n);
    },
    [activeItems],
  );

  const selectActive = React.useCallback(() => {
    const item = activeItems[Math.min(activeIndex, activeItems.length - 1)];
    if (item) toggleSelection(item, activePool);
  }, [activeItems, activePool, activeIndex, toggleSelection]);

  const handleSetActivePool = React.useCallback(
    (poolId: string) => {
      setActivePool(poolId);
      requestAnimationFrame(() => {
        focusInput();
      });
    },
    [focusInput],
  );

  const contextValue: EntitySelectorContextValue = React.useMemo(
    () => ({
      config,
      variant,
      selections,
      toggleSelection,
      isSelected,
      clearSelections,
      activePool,
      setActivePool: handleSetActivePool,
      search,
      setSearch,
      open: effectiveOpen,
      onOpenChange: setOpen,
      getPool,
      getPoolBadge,
      getFilteredItems,
      getPinnedItems,
      isSectionCollapsed,
      toggleSection,
      activeIndex: clampedActiveIndex,
      activeId,
      setActiveIndex: activateFromPointer,
      isActiveFromPointer,
      moveActive,
      selectActive,
      inputRef,
      focusInput,
    }),
    [
      config,
      variant,
      selections,
      toggleSelection,
      isSelected,
      clearSelections,
      activePool,
      handleSetActivePool,
      search,
      setSearch,
      effectiveOpen,
      setOpen,
      getPool,
      getPoolBadge,
      getFilteredItems,
      getPinnedItems,
      isSectionCollapsed,
      toggleSection,
      clampedActiveIndex,
      activeId,
      activateFromPointer,
      isActiveFromPointer,
      moveActive,
      selectActive,
      focusInput,
    ],
  );

  React.useImperativeHandle(
    ref,
    () => ({
      move: moveActive,
      selectActive,
      getActiveId: () => activeId,
      cycleActivePool: (delta: number) => {
        const pools = config.pools;
        if (pools.length < 2) return;
        const idx = Math.max(
          0,
          pools.findIndex((p) => p.id === activePool),
        );
        const next =
          pools[(((idx + delta) % pools.length) + pools.length) % pools.length];
        if (next) handleSetActivePool(next.id);
      },
    }),
    [
      moveActive,
      selectActive,
      activeId,
      config.pools,
      activePool,
      handleSetActivePool,
    ],
  );

  if (isInline) {
    return (
      <EntitySelectorProvider value={contextValue}>
        {children}
      </EntitySelectorProvider>
    );
  }

  if (variant === "drawer") {
    return (
      <Drawer
        open={open}
        onOpenChange={(next) => setOpen(next)}
        // Focus the search input only after the open animation settles. Focusing
        // mid-animation opens the mobile virtual keyboard, which resizes the
        // visual viewport and desyncs the drawer's translate math — produces a
        // top clip and locks list scroll until a reflow.
        onOpenChangeComplete={(next) => {
          if (next) focusInput();
        }}
        snapPoints={DRAWER_SNAP_POINTS}
        defaultSnapPoint={DRAWER_SNAP_POINTS[1]}
      >
        <EntitySelectorProvider value={contextValue}>
          {children}
        </EntitySelectorProvider>
      </Drawer>
    );
  }

  return (
    <Popover open={open} onOpenChange={(next) => setOpen(next)}>
      <EntitySelectorProvider value={contextValue}>
        {children}
      </EntitySelectorProvider>
    </Popover>
  );
}

EntitySelectorRoot.displayName = "EntitySelector";

export { EntitySelectorRoot };
